import { IActionRepeaterValue, isEmail, TypographyGroupValue } from '@makyoapp/makyo-common'
import { ColorVariant, HoverEffectVariant, HoverIntensity, Newsletter, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

interface CompNewsletterWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
}

export function CompNewsletterWrapperCore({
  infos: { component, settings, currentParentBg, containerClient, detailTableId, detailRowIndex, rowId: detailRowId },
  action: { simulateAction, isLoading },
}: CompNewsletterWrapperCoreProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const { isPhonePortrait } = useBreakpoint()

  const placeholder = useParseVariableText(
    settings[ComponentSettingKey.NewsletterDataPlaceholder],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const buttonText = useParseVariableText(
    settings[ComponentSettingKey.NewsletterDataButtonText],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const icon = getSafely(settings, [ComponentSettingKey.NewsletterDataIcon], 'mail')
  const email = useParseVariableSelect(
    settings?.[ComponentSettingKey.NewsletterDataEmail],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const title = useParseVariableText(
    settings[ComponentSettingKey.NewsletterDataCompTitle],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const buttonColorType = getSafely(settings, [ComponentSettingKey.NewsletterDesignButtonColor], 'solid')
  const buttonColorSolid = settings?.[ComponentSettingKey.NewsletterDesignButtonColorSolid]
  const buttonColorStartGradient = settings?.[ComponentSettingKey.NewsletterDesignButtonColorStartGradient]
  const buttonColorEndGradient = settings?.[ComponentSettingKey.NewsletterDesignButtonColorEndGradient]
  const buttonColorGradientDirection = settings?.[ComponentSettingKey.NewsletterDesignButtonColorGradientDirection]
  const borderColor = settings?.[ComponentSettingKey.NewsletterDesignBorderColor]

  const compTitleColorType = getSafely(
    settings,
    [ComponentSettingKey.NewsletterDesignCompTitleColor],
    'solid'
  ) as ColorVariant
  const compTitleColorSolid = settings?.[ComponentSettingKey.NewsletterDesignCompTitleColorSolid]
  const compTitleColorStartGradient = settings?.[ComponentSettingKey.NewsletterDesignCompTitleColorStartGradient]
  const compTitleColorEndGradient = settings?.[ComponentSettingKey.NewsletterDesignCompTitleColorEndGradient]
  const compTitleColorGradientDirection =
    settings?.[ComponentSettingKey.NewsletterDesignCompTitleColorGradientDirection]

  const size = settings?.[ComponentSettingKey.NewsletterDesignSize]
  const width = settings?.[ComponentSettingKey.NewsletterDesignWidth]
  const alignment = settings?.[ComponentSettingKey.NewsletterDesignAlignment]
  const margin = settings?.[ComponentSettingKey.NewsletterDesignMargin]
  const padding = settings?.[ComponentSettingKey.NewsletterDesignPadding]
  const iconSize = settings?.[ComponentSettingKey.NewsletterDesignIconWidth]?.[0]

  const typoSettings = settings[ComponentSettingKey.NewsletterDesignTypography] as TypographyGroupValue
  const titleTypography = typoSettings?.[ComponentSettingKey.NewsletterDesignComponentTitleTypography]
  const inputTypography = typoSettings?.[ComponentSettingKey.NewsletterDesignInputTypography]
  const buttonTypography = typoSettings?.[ComponentSettingKey.NewsletterDesignButtonTypography]

  const compTitleAlignment = settings?.[ComponentSettingKey.NewsletterDesignCompTitleAlignment]
  const compTitleMargin = settings?.[ComponentSettingKey.NewsletterDesignCompTitleMargin]

  const hoverEffect = settings[ComponentSettingKey.NewsletterDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  const subscribeClickActions = settings?.[
    ComponentSettingKey.NewsletterActionOnClickSubscribe
  ] as IActionRepeaterValue[]

  const handleSubmit = (email: string) => {
    // check if the email is valid
    if (!email || !email.trim().length || !isEmail(email)) return

    // check if has actions
    if (!subscribeClickActions || subscribeClickActions.length === 0) return

    simulateAction({
      actions: subscribeClickActions,
      external: {
        newsletter: {
          email,
        },
        detailsPage: {
          detailTableId,
          detailRowIndex,
          detailRowId,
        },
      },
      triggerSource: {
        id: component._id,
        label: component._Title || component._ComponentID,
        type: 'component',
      },
    })
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const newsletterComponent = (
    <Newsletter
      icon={icon}
      backgroundColor={currentParentBg}
      buttonColorType={buttonColorType as any}
      buttonColorSolid={buttonColorSolid}
      buttonColorStartGradient={buttonColorStartGradient}
      buttonColorEndGradient={buttonColorEndGradient}
      buttonColorGradientDirection={buttonColorGradientDirection}
      placeholder={placeholder}
      buttonText={buttonText}
      size={size}
      width={width}
      borderColor={borderColor}
      showBorder={!!borderColor && !isPhonePortrait}
      onSubmit={handleSubmit}
      alignment={alignment}
      margin={margin}
      defaultEmail={email as string}
      loading={isLoading}
      padding={(padding || '10') as RawSpacingInput}
      titleTypography={titleTypography}
      inputTypography={inputTypography}
      buttonTypography={buttonTypography}
      title={title}
      titleColorType={compTitleColorType as any}
      titleColorSolid={compTitleColorSolid}
      titleColorStartGradient={compTitleColorStartGradient}
      titleColorEndGradient={compTitleColorEndGradient}
      titleColorGradientDirection={compTitleColorGradientDirection}
      titleAlignment={compTitleAlignment}
      titleMargin={compTitleMargin}
      iconSize={iconSize}
      hoverEffect={hoverEffect as HoverEffectVariant}
      hoverIntensity={hoverIntensity as HoverIntensity}
    />
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative flex flex-col w-full border border-dashed' style={{ borderColor: '#333' }}>
        {newsletterComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return newsletterComponent
}
